import * as z from "zod";
import { userFacingError } from "../authorization";
import { syncAllFacets } from "../commitFiles";
import { ensureWritable } from "../copyOnWrite";
import { isFeatureFlagEnabled } from "../featureFlags";
import { STEP_TESTS_FEATURE_FLAG } from "../featureFlagNames";
import {
  isValidStepTestName,
  STEP_TEST_CASES_DIR,
} from "../stepTests/constants";
import { mutator } from "./mutator";
import {
  deleteStepTestClockFile,
  deleteStepTestRequestsFile,
  deleteStepTestVolumeFixtureFiles,
} from "./stepTestCommitFiles";
import { branchSchema, insertUserAction } from "./utils";

export const unpinStepTestSchema = z
  .object({
    workflowId: z.string(),
    stepDir: z.string(),
    name: z.string(),
  })
  .merge(branchSchema);

export const unpinStepTest = mutator({
  schema: unpinStepTestSchema,
  resolve: {
    branch: { kind: "branch", from: "branchId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const {
      branch,
      stepDir,
      name,
      forkBranchId,
      forkCommitId,
      forkChatId,
      workingCommitId,
      workingChatId,
    } = args;
    if (
      !(await isFeatureFlagEnabled(tx, STEP_TESTS_FEATURE_FLAG, ctx.tenantId))
    ) {
      throw userFacingError("Step tests are disabled");
    }
    if (!isValidStepTestName(name)) {
      throw userFacingError(
        "Step test names may only contain letters, numbers, dashes, and underscores"
      );
    }
    if (branch.name === "main") {
      throw userFacingError("Cannot remove step tests from the live workflow");
    }
    const { commitId } = await ensureWritable(
      tx,
      ctx,
      ctx.tenantId,
      {
        branchId: branch.id,
        forkBranchId,
        forkCommitId,
        forkChatId,
        workingCommitId,
        workingChatId,
      },
      branch
    );
    const base = `${stepDir}/${STEP_TEST_CASES_DIR}/${name}`;
    await tx.mutate.commitFiles.delete({ commitId, path: `${base}.input` });
    await tx.mutate.commitFiles.delete({ commitId, path: `${base}.output` });
    await tx.mutate.commitFiles.delete({ commitId, path: `${base}.meta` });
    await deleteStepTestClockFile({ tx, commitId, stepDir, name });
    await deleteStepTestRequestsFile({ tx, commitId, stepDir, name });
    await deleteStepTestVolumeFixtureFiles({ tx, commitId, stepDir, name });
    await syncAllFacets({ tx, commitId, tenantId: ctx.tenantId });
    await insertUserAction(tx, commitId, ctx, ctx.tenantId, "unpinStepTest", {
      stepDir,
      name,
    });
  },
});
